import { useState } from 'react';
import type { Key } from 'react';
import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import { SearchOutlined, StarFilled, StopOutlined } from '@ant-design/icons';
import type {
  AvailabilityStatus,
  CollectionKind,
  PublicationListOut,
} from '@/api/generated/schemas';
import { ModStatus } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { NumberRangeFilter } from '@/components/common/NumberRangeFilter';
import { SelectFilter } from '@/components/common/SelectFilter';
import { PublicationCoverThumb } from '@/components/user_catalog/publications/PublicationCoverThumb';
import { PublicationDrawerIcon } from '@/components/user_catalog/publications/drawer/PublicationDrawerIcon';
import { useCollectionExclusion } from '@/hooks/user_catalog/useCollectionExclusion';
import { useAdminTable } from '@/hooks/useAdminTable';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useTableSearch } from '@/hooks/useTableSearch';
import { availabilityStatusColors } from '@/pages/user_catalog/publications/constants';
import { isFieldFiltered } from '@/utils/filters';
import { abbreviatePersonName } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

const { Text } = Typography;

const COVER_SIZE = 48;

/** The endpoint caps `limit` at 100. */
const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];

interface Props {
  /** Collectionable id — every collection resource's own `record.id`. */
  collectionId: number;
  /** Decides whether publications can be excluded from this collection. */
  collectionKind: CollectionKind;
}

/**
 * Publications a collection resolves to, straight from
 * `GET /user_catalog/collections/{id}/publications` — server paginated,
 * sortable and filterable. Shared by every collection show view.
 */
export const CollectionPublicationsTable = ({
  collectionId,
  collectionKind,
}: Props) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch({
    syncWithLocation: false,
  });
  const { excludedField, excludeMany, isLoading } = useCollectionExclusion({
    collectionId,
    collectionKind,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

  const { tableProps, searchFormProps, setFilters, filters, sorters } =
    useAdminTable<PublicationListOut>({
      // Nested endpoint: the dot notation becomes the URL path, and this
      // route — unlike the flat resources — has no trailing slash.
      resource: `user_catalog.collections.${collectionId}.publications`,
      meta: { trailingSlash: false },
      pagination: { pageSize: 20 },
      syncWithLocation: false,
      onSearch,
      // No initial sorter on purpose: without `sort` the API returns the
      // collection's own order (ranking positions, curated order, …).
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  // Take the row-navigation props first: they carry the `showTotal` counter,
  // so the page-size override below has to build on them, not on `tableProps`.
  const navTableProps = rowNav('user_catalog.publications', tableProps);

  // Only rows of the loaded page can be excluded — the request needs each
  // publication's own `excluded_*_ids`, which the row itself carries.
  const selectedRows = (tableProps.dataSource ?? []).filter((row) =>
    selectedRowKeys.includes(row.id)
  );

  const publicationCell = (record: PublicationListOut) => {
    const authors = (record.publication_authors ?? [])
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((item) =>
        item.author?.name ? abbreviatePersonName(item.author.name) : null
      )
      .filter(Boolean)
      .join(', ');

    return (
      <Flex gap={token.marginSM} align="flex-start">
        <PublicationCoverThumb url={record.cover_url} size={COVER_SIZE} />
        <div style={{ minWidth: 0 }}>
          <Space size={4}>
            <Text strong>{record.title}</Text>
            <PublicationDrawerIcon publicationId={record.id} />
          </Space>
          <div>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {record.publisher?.name ?? `#${record.publisher_id}`}
            </Text>
          </div>
          {authors && (
            <div>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {authors}
              </Text>
            </div>
          )}
        </div>
      </Flex>
    );
  };

  return (
    <Card>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('user_catalog.publications.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {selectedRowKeys.length > 0 && (
        <Space style={{ marginBottom: 16 }}>
          <span>
            {t('user_catalog.common.bulk_mod_status.selected', {
              count: selectedRowKeys.length,
            })}
          </span>
          <Button
            danger
            type="primary"
            icon={<StopOutlined />}
            loading={isLoading}
            onClick={() =>
              excludeMany(selectedRows, () => setSelectedRowKeys([]))
            }
          >
            {t('user_catalog.common.collection_exclusion.button')}
          </Button>
        </Space>
      )}

      <Table
        {...navTableProps}
        rowKey="id"
        size="small"
        rowSelection={
          excludedField
            ? { selectedRowKeys, onChange: setSelectedRowKeys }
            : undefined
        }
        pagination={
          navTableProps.pagination === false
            ? false
            : {
                ...navTableProps.pagination,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
              }
        }
      >
        <Table.Column
          dataIndex="title"
          title={t('user_catalog.publications.fields.publication')}
          sorter
          sortOrder={getSortOrder(sorters, 'title')}
          render={(_, record: PublicationListOut) => publicationCell(record)}
        />

        <Table.Column
          dataIndex="total_rating"
          title={`${t('user_catalog.publications.fields.total_rating')} / ${t('user_catalog.publications.fields.reviews_count')}`}
          sorter
          sortOrder={getSortOrder(sorters, 'total_rating')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'total_rating')}
          filterDropdown={({ confirm, clearFilters }) => (
            <NumberRangeFilter
              field="total_rating"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(_: unknown, record: PublicationListOut) => (
            <Space>
              <StarFilled style={{ color: '#fadb14' }} />
              {`${record.total_rating ?? '-'} / ${record.reviews_count ?? '-'}`}
            </Space>
          )}
        />

        <Table.Column
          dataIndex="search_popularity"
          title={t('user_catalog.common.fields.search_popularity')}
          sorter
          sortOrder={getSortOrder(sorters, 'search_popularity')}
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'search_popularity'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <NumberRangeFilter
              field="search_popularity"
              min={0}
              max={10}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(v: number) => v ?? 0}
        />

        <Table.Column
          dataIndex="mod_status"
          title={t('base.fields.mod_status')}
          sorter
          sortOrder={getSortOrder(sorters, 'mod_status')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'mod_status')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="mod_status"
              placeholder={t('base.fields.mod_status')}
              options={Object.values(ModStatus).map((value) => ({
                label: t(`base.mod_status.${value}`),
                value,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) => <ModStatusTag value={value} />}
        />

        <Table.Column
          dataIndex="availability_status"
          title={t('user_catalog.publications.fields.availability_status')}
          render={(value: AvailabilityStatus | null | undefined) =>
            value ? (
              <Tag color={availabilityStatusColors[value] || 'default'}>
                {t(`user_catalog.publications.availability_status.${value}`)}
              </Tag>
            ) : (
              '-'
            )
          }
        />
      </Table>
    </Card>
  );
};
